// Lógica centralizada para determinar si un concurso está realmente
// disponible para el usuario, combinando su status/contest_state con sus
// fechas reales (submission_start, submission_deadline, voting_start,
// voting_end, winner_announcement). Se usa tanto en el panel de
// administración (/pet_contest) como en la página pública de inscripción
// (/contest_participant) para que ambas vistas sean coherentes entre sí.

export type ContestAvailabilityReasonKey =
  | 'Cancelled'
  | 'Rejected'
  | 'PendingApproval'
  | 'Completed'
  | 'VotingEnded'
  | 'SubmissionsClosed'
  | 'NotStarted'
  | 'AwaitingVoting'
  | 'VotingOpen'
  | 'SubmissionsOpen'
  | 'Active';

export interface ContestAvailability {
  available: boolean;
  reasonKey: ContestAvailabilityReasonKey;
  /** Fecha ISO relevante para el motivo (si aplica), sin formatear. */
  date?: string;
}

const parseDate = (value: any): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Determina disponibilidad real de un concurso. Prioriza estados
 * administrativos explícitos (cancelado, rechazado, pendiente) y luego
 * evalúa las fechas del ciclo de vida del concurso.
 */
export const getContestAvailability = (contest: any): ContestAvailability => {
  const now = Date.now();
  const contestState = (contest?.contest_state || '').toLowerCase();
  const status = (contest?.status || '').toLowerCase();
  const phase = (contest?.phase || '').toLowerCase();

  if (contestState === 'cancelled' || contestState === 'cancel' || status === 'cancelled') {
    return { available: false, reasonKey: 'Cancelled' };
  }
  if (status === 'rejected') {
    return { available: false, reasonKey: 'Rejected' };
  }
  if (status === 'pending') {
    return { available: false, reasonKey: 'PendingApproval' };
  }

  const submissionStart = parseDate(contest?.submission_start);
  const submissionDeadline = parseDate(contest?.submission_deadline);
  const votingStart = parseDate(contest?.voting_start);
  const votingEnd = parseDate(contest?.voting_end);
  const winnerAnnouncement = parseDate(contest?.winner_announcement);

  if (status === 'completed' || phase === 'completed') {
    return { available: false, reasonKey: 'Completed', date: contest?.winner_announcement };
  }
  if (votingEnd && now > votingEnd) {
    return { available: false, reasonKey: 'VotingEnded', date: contest?.voting_end };
  }
  if (winnerAnnouncement && now > winnerAnnouncement) {
    return { available: false, reasonKey: 'Completed', date: contest?.winner_announcement };
  }
  // Sin fase de votación configurada: el cierre de inscripciones marca el fin del concurso
  if (!votingStart && !votingEnd && submissionDeadline && now > submissionDeadline) {
    return { available: false, reasonKey: 'SubmissionsClosed', date: contest?.submission_deadline };
  }

  if (submissionStart && now < submissionStart) {
    return { available: false, reasonKey: 'NotStarted', date: contest?.submission_start };
  }

  if (votingStart && now >= votingStart && (!votingEnd || now <= votingEnd)) {
    return { available: true, reasonKey: 'VotingOpen', date: contest?.voting_end };
  }
  if (submissionDeadline && now <= submissionDeadline) {
    return { available: true, reasonKey: 'SubmissionsOpen', date: contest?.submission_deadline };
  }
  if (submissionDeadline && now > submissionDeadline && votingStart && now < votingStart) {
    return { available: false, reasonKey: 'AwaitingVoting', date: contest?.voting_start };
  }

  return { available: true, reasonKey: 'Active' };
};

/** Mensajes en español listos para usar en páginas sin i18n (ej. página pública). */
const DEFAULT_MESSAGES_ES: Record<ContestAvailabilityReasonKey, (date: string) => string> = {
  Cancelled: () => 'No disponible: este concurso fue cancelado por el organizador.',
  Rejected: () => 'No disponible: este concurso fue rechazado.',
  PendingApproval: () => 'No disponible todavía: el concurso está pendiente de aprobación.',
  Completed: (date) => `No disponible: este concurso ya finalizó${date ? ` (ganadores anunciados el ${date})` : ''}.`,
  VotingEnded: (date) => `No disponible: la votación finalizó el ${date}.`,
  SubmissionsClosed: (date) => `No disponible: las inscripciones cerraron el ${date}.`,
  NotStarted: (date) => `No disponible todavía: este concurso abre inscripciones el ${date}.`,
  AwaitingVoting: (date) => `No disponible: las inscripciones cerraron y la votación abrirá el ${date}.`,
  VotingOpen: (date) => `Disponible: votación abierta${date ? ` hasta el ${date}` : ''}.`,
  SubmissionsOpen: (date) => `Disponible: inscripciones abiertas${date ? ` hasta el ${date}` : ''}.`,
  Active: () => 'Disponible: el concurso está activo.',
};

const DEFAULT_MESSAGES_EN: Record<ContestAvailabilityReasonKey, (date: string) => string> = {
  Cancelled: () => 'Unavailable: this contest was cancelled by the organizer.',
  Rejected: () => 'Unavailable: this contest was rejected.',
  PendingApproval: () => 'Not available yet: the contest is pending approval.',
  Completed: (date) => `Unavailable: this contest has ended${date ? ` (winners announced on ${date})` : ''}.`,
  VotingEnded: (date) => `Unavailable: voting ended on ${date}.`,
  SubmissionsClosed: (date) => `Unavailable: submissions closed on ${date}.`,
  NotStarted: (date) => `Not available yet: submissions open on ${date}.`,
  AwaitingVoting: (date) => `Unavailable: submissions closed; voting opens on ${date}.`,
  VotingOpen: (date) => `Available: voting is open${date ? ` until ${date}` : ''}.`,
  SubmissionsOpen: (date) => `Available: submissions are open${date ? ` until ${date}` : ''}.`,
  Active: () => 'Available: the contest is active.',
};

/**
 * Devuelve disponibilidad + mensaje ya formateado en español, útil para
 * páginas públicas sin sistema de i18n propio.
 */
export const describeContestAvailability = (
  contest: any,
  formatDate: (iso: string) => string = (iso) => new Date(iso).toLocaleDateString('es-ES')
): { available: boolean; message: string; messageEn: string; reasonKey: ContestAvailabilityReasonKey } => {
  const info = getContestAvailability(contest);
  const formattedDate = info.date ? formatDate(info.date) : '';
  return {
    available: info.available,
    reasonKey: info.reasonKey,
    message: DEFAULT_MESSAGES_ES[info.reasonKey](formattedDate),
    messageEn: DEFAULT_MESSAGES_EN[info.reasonKey](formattedDate),
  };
};
